import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'wouter';
import { 
  Home, 
  BarChart3, 
  Package, 
  Users, 
  Award, 
  FileText, 
  LogOut, 
  User, 
  ChevronLeft,
  Menu,
  X,
  Sparkles,
  BookOpen,
  DollarSign,
  Share2
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useState } from 'react';
import BearFluencerBadge from './BearFluencerBadge';

interface InfluencerNavigationProps {
  currentPage?: string;
  showBackButton?: boolean;
}

export default function InfluencerNavigation({ currentPage, showBackButton = false }: InfluencerNavigationProps) {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getUserInitials = () => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const navItems = [
    {
      href: '/influencer/dashboard',
      icon: BarChart3,
      label: 'Dashboard',
      shortLabel: 'Dashboard'
    },
    {
      href: '/influencer/resources',
      icon: Package,
      label: 'Brand Resources',
      shortLabel: 'Resources'
    },
    {
      href: '/influencer/application',
      icon: FileText,
      label: 'Application',
      shortLabel: 'Apply'
    },
  ];

  const quickLinks = [
    {
      href: '/home',
      icon: Home,
      label: 'Create Stories'
    },
    {
      href: '/profile',
      icon: User,
      label: 'My Profile'
    },
    {
      href: '/community',
      icon: Users,
      label: 'Community'
    }
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:block bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {showBackButton && (
                <Button
                  onClick={() => window.history.back()}
                  size="sm"
                  variant="ghost"
                  className="hover:bg-white/20 text-white"
                  data-testid="button-back"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              
              <div className="flex items-center space-x-2">
                <Sparkles className="h-6 w-6" />
                <h1 className="text-xl font-bold">BearFluencer Hub</h1>
                <BearFluencerBadge size="small" showTooltip={true} variant="minimal" />
              </div>

              <div className="flex items-center space-x-1">
                {navItems.map((item) => {
                  const isActive = location === item.href;
                  return (
                    <Button
                      key={item.href}
                      asChild
                      variant={isActive ? "secondary" : "ghost"}
                      size="sm"
                      className={isActive ? "bg-white/20 text-white" : "hover:bg-white/20 text-white"}
                      data-testid={`nav-button-${item.shortLabel.toLowerCase()}`}
                    >
                      <Link href={item.href}>
                        <item.icon className="h-4 w-4 mr-2" />
                        {item.label}
                      </Link>
                    </Button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Quick Links Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hover:bg-white/20 text-white">
                    <Share2 className="h-4 w-4 mr-2" />
                    Quick Links
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {quickLinks.map((link) => (
                    <DropdownMenuItem key={link.href} asChild>
                      <Link href={link.href} className="cursor-pointer">
                        <link.icon className="h-4 w-4 mr-2" />
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center space-x-2 hover:bg-white/20"
                    data-testid="button-user-menu"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-white/20 text-white text-xs">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline">{user?.firstName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="cursor-pointer">
                      <User className="h-4 w-4 mr-2" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/influencer/dashboard" className="cursor-pointer">
                      <DollarSign className="h-4 w-4 mr-2" />
                      Earnings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => logoutMutation.mutate()}
                    className="cursor-pointer text-red-600"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {currentPage && (
            <div className="mt-4 pb-2">
              <h2 className="text-2xl font-bold">{currentPage}</h2>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="md:hidden bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5" />
              <span className="font-bold">BearFluencer</span>
              <BearFluencerBadge size="small" showTooltip={false} variant="minimal" />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="hover:bg-white/20 text-white"
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>

          {currentPage && (
            <div className="mt-2">
              <h2 className="text-lg font-semibold">{currentPage}</h2>
            </div>
          )}
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-white/20 bg-purple-700/50 backdrop-blur">
            <div className="px-4 py-2 space-y-1">
              {navItems.map((item) => {
                const isActive = location === item.href;
                return (
                  <Button
                    key={item.href}
                    asChild
                    variant="ghost"
                    size="sm"
                    className={`w-full justify-start ${
                      isActive ? "bg-white/20" : "hover:bg-white/10"
                    } text-white`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Link href={item.href}>
                      <item.icon className="h-4 w-4 mr-2" />
                      {item.label}
                    </Link>
                  </Button>
                );
              })}

              <div className="border-t border-white/20 pt-2 mt-2">
                <p className="text-xs font-semibold text-white/70 mb-1 px-2">Quick Links</p>
                {quickLinks.map((link) => (
                  <Button
                    key={link.href}
                    asChild
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start hover:bg-white/10 text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Link href={link.href}>
                      <link.icon className="h-4 w-4 mr-2" />
                      {link.label}
                    </Link>
                  </Button>
                ))}
              </div>

              <div className="border-t border-white/20 pt-2 mt-2">
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start hover:bg-white/10 text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link href="/profile">
                    <User className="h-4 w-4 mr-2" />
                    Profile Settings
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start hover:bg-white/10 text-red-300"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logoutMutation.mutate();
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}